import { Prompt } from "../components/PredefinedPrompts";

/**
 * Get admin user IDs from environment variable
 * @returns Array of admin user IDs
 */
export const getAdminUserIds = (): number[] => {
  const adminIds = import.meta.env.VITE_ADMIN_USER_IDS;
  if (!adminIds) return [];

  return adminIds
    .split(",")
    .map((id: string) => parseInt(id.trim(), 10))
    .filter((id: number) => !isNaN(id));
};

/**
 * Check if a user ID is an admin
 * @param userId - The user ID to check
 * @returns boolean indicating if the user is an admin
 */
export const isAdminUser = (userId: number | undefined): boolean => {
  if (userId === undefined) return false;
  return getAdminUserIds().includes(userId);
};

export const defaultSteps = [{ id: "st1", title: "Thinking" }];
export const prSteps = [
  {
    id: "pr1",
    title: "Retrieving historical press releases and your brand tone",
  },
  {
    id: "pr2",
    title: "Drafting the press release in chosen press style",
  },
  {
    id: "pr3",
    title: "Reviewing the draft for brand alignment and factual accuracy",
  },
  {
    id: "pr4",
    title: "Iterating between drafting and review",
  },
  {
    id: "pr5",
    title: "Finalizing the press release",
  },
];
export const localizerSteps = [
  { id: "a1", title: "Translating the press release" },
  { id: "a2", title: "Reviewing for cultural nuances" },
  { id: "a3", title: "Iterating between drafting and review" },
  { id: "a4", title: "Finalizing the press release" },
];
export const mediaBriefSteps = [
  { id: "b1", title: "Retrieving historical media brief and Q&A" },
  { id: "b2", title: "Drafting key questions" },
  { id: "b3", title: "Drafting executive responses" },
  { id: "b4", title: "Identifying responses which require human review" },
  { id: "b5", title: "Finalizing the media brief" },
];
export const socialMediaStepsWithImage = [
  { id: "c1", title: "Analyzing historical social media posts" },
  { id: "c2", title: "Initiating social media agent" },
  {
    id: "c3",
    title: "Reviewing the posts for tone alignment, structure, and compliance",
  },
  { id: "c4", title: "Iterating and generating the media scores" },
  { id: "c5", title: "Generating images for social media posts" },
  { id: "c6", title: "Finalizing the social media posts" },
];
export const socialMediaStepsWithoutImage = [
  { id: "c1", title: "Analyzing historical social media posts" },
  { id: "c2", title: "Initiating social media agent" },
  {
    id: "c3",
    title: "Reviewing the posts for tone alignment, structure, and compliance",
  },
  { id: "c4", title: "Iterating and generating the media scores" },
  { id: "c5", title: "Finalizing the social media posts" },
];

export const internalComSteps = [
  { id: "d1", title: "Retrieving past internal emails" },
  { id: "d2", title: "Drafting and incorporating internal communication tone" },
  { id: "d3", title: "Finalizing the executive email" },
];

export const syntheticResumeSteps = [
  { id: "sr1", title: "Mapping role to core functional and technical skills" },
  { id: "sr2", title: "Identifying future-ready skills" },
  { id: "sr3", title: "Establishing required qualification" },
  { id: "sr4", title: "Generating preferred qualification" },
  { id: "sr5", title: "Designing first-interview screening questions" },
];

export const modifyRequirementsSteps = [
  { id: "mr1", title: "Updating the required changes as per your input" },
];

export const resumeFetcherSteps = [
  { id: "rf1", title: "Searching candidate database" },
  { id: "rf2", title: "Filtering candidates by qualifications" },
  { id: "rf3", title: "Retrieving candidate profiles" },
  { id: "rf4", title: "Finalizing candidate list" },
];

export const scoringSteps = [
  {
    id: "sc1",
    title: "Screening resumes on functional skills & preferred qualifications",
  },
  { id: "sc2", title: "Applying historical interview feedback, if available" },
  { id: "sc3", title: "Overlaying current role, title, location" },
  { id: "sc4", title: "Evaluation based on first round interview fit" },
];

export const emailDraftingSteps = [
  { id: "ed1", title: "Analyzing candidate profiles" },
  { id: "ed2", title: "Drafting personalized emails" },
  { id: "ed3", title: "Reviewing email content" },
  { id: "ed4", title: "Finalizing email drafts" },
];

export const sowAutoFillerSteps = [
  { id: "sow1", title: "Preparing contract workspace" },
  {id:"sow2", title: "Detecting PII"},
  { id: "sow3", title: "Validating contract name" },
  { id: "sow4", title: "Setting up document structure" },
];

export const sowPreAnalyzerSteps = [
  { id: "spa1", title: "Analyzing proposal document" },
  { id: "spa2", title: "Detecting business unit and category" },
  { id: "spa3", title: "Identifying contract type" },
  { id: "spa4", title: "Searching for matching template" },
];

export const sowDataModifierSteps = [
  { id: "sdm1", title: "Processing modification requests" },
  { id: "sdm2", title: "Validating changes" },
  { id: "sdm3", title: "Updating SOW fields" },
  { id: "sdm4", title: "Regenerating document" },
];

export const sowQuestionAnswererSteps = [
  { id: "sqa1", title: "Understanding your question" },
  { id: "sqa2", title: "Searching document context" },
  { id: "sqa3", title: "Preparing detailed answer" },
];

export const sowPiiDetectorSteps = [
  { id: "spd1", title: "Checking for sensitive information" },
  { id: "spd2", title: "Analyzing document content for PII" },
  { id: "spd3", title: "Compiling detection results" },
];

export const sowFinalGeneratorSteps = [
  { id: "sfg1", title: "Finalizing SOW content" },
  { id: "sfg2", title: "Generating DOCX document" },
  { id: "sfg3", title: "Validating output" },
  { id: "sfg4", title: "Preparing download" },
];

export const translationAgentSteps = [
  {
    id: "l1",
    title: "Identifying the source and target language",
  },
  { id: "l2", title: "Understanding the content type" },
  { id: "l3", title: "Translating with appropriate tone and terminology" },
  { id: "l4", title: "Reviewing for accuracy, clarity, and consistency" },
  { id: "l5", title: "Delivering the finalized content" },
];

export const jdComposerSteps = [
  { id: "jdc1", title: "Analyzing role requirements and job description" },
  { id: "jdc2", title: "Composing job overview and responsibilities" },
  { id: "jdc3", title: "Mapping core functional and technical skills" },
  { id: "jdc4", title: "Identifying future-ready skills" },
  { id: "jdc5", title: "Establishing required and preferred qualifications" },
];

export const checklistAgentSteps = [
  { id: "cl1", title: "Generating business & commercial checklist" },
  { id: "cl2", title: "Generating financial & payment checklist" },
  { id: "cl3", title: "Generating procurement & vendor governance checklist" },
  {
    id: "cl4",
    title: "Generating legal, security & regulatory compliance checklist",
  },
];

export const TemplateReviewerAgentSteps = [
  { id: "cl1", title: "Reviewing against business checklist" },
  { id: "cl2", title: "Reviewing against finance checklist" },
  { id: "cl3", title: "Reviewing against procurement checklist" },
  { id: "cl4", title: "Reviewing against legal checklist" },
  { id: "cl5", title: "Creating summary" },
  { id: "cl6", title: "Generating output & recommendation" },
];

export const ContractReviewerQAAgentSteps = [
  { id: "crqa1", title: "Understanding your question" },
  { id: "crqa2", title: "Searching contract review data" },
  { id: "crqa3", title: "Preparing detailed answer" },
];

// export const BusinessReviewerSteps = [
//   { id: "cr1", title: "Business reviewer initialised" },
// ];

// export const FinanceReviewerSteps = [
//   { id: "cr2", title: "Finance reviewer initialised" },
// ];

// export const ProcurementReviewerSteps = [
//   { id: "cr3", title: "Procurement reviewer initialised" },
// ];

// export const LegalReviewerSteps = [
//   { id: "cr4", title: "Legal reviewer initialised" },
// ];

// export const ContractAnalysisSteps = [
//   { id: "cr5", title: "Analysing deviations, key gaps & risks" },
//   { id: "cr6", title: "Understanding negotiation positions" },
// ];

export const ContractReviewerAgentSteps = [
  { id: "cr1", title: "Initiating business review" },
  { id: "cr2", title: "Initiating finance review" },
  { id: "cr3", title: "Initiating procurement review" },
  { id: "cr4", title: "Initiating legal review" },
  { id: "cr5", title: "Analysing deviations, key gaps & risks" },
  { id: "cr6", title: "Generating negotiation points" },
];

export const ConsolidatorAgentSteps = [
  { id: "cn1", title: "Consolidator agent initiated" },
  { id: "cn2", title: "Consolidating negotiation playbook across functions" },
  { id: "cn3", title: "Generating negotiation themes & bundling suggestions" },
];

export const ConsolidatorAgentSupplierCommSteps = [
  { id: "cs1", title: "Analysing consolidated summary" },
  { id: "cs2", title: "Generating supplier communication" },
];

export const RedraftingAgentSteps = [
  { id: "rd1", title: "Analyzing negotiation playbook" },
  { id: "rd2", title: "Analyzing contract document" },
];

export const LegalReviewerAgentSteps = [
  { id: "lr1", title: "Initiating legal review" },
  { id: "lr2", title: "Analyzing legal clauses and terms" },
  { id: "lr3", title: "Checking regulatory compliance" },
  { id: "lr4", title: "Identifying legal risks and gaps" },
  { id: "lr5", title: "Generating legal recommendations" },
];

export const TemplateGuidedIntakeAgentSteps = [
  { id: "ti1", title: "Gathering business context and contract type" },
  { id: "ti2", title: "Collecting use case specific requirements" },
  { id: "ti3", title: "Capturing contract terms and conditions" },
];

export const TemplateDocumentSelectorSteps = [
  { id: "ts1", title: "Identifying business unit and template options" },
];

export const TemplateDrafterSteps = [
  { id: "td1", title: "Processing collected contract details" },
  { id: "td2", title: "Populating template with your inputs" },
  { id: "td3", title: "Generating filled contract template" },
  { id: "td4", title: "Creating contract summary PDF" },
];

export const TemplateReviewerQASteps = [
  { id: "tq1", title: "Processing your modification requests" },
  { id: "tq2", title: "Updating contract template fields" },
  { id: "tq3", title: "Regenerating updated contract PDF" },
];

export const TemplateRequestOrchestratorSteps = [
  { id: "to1", title: "Understanding your procurement need" },
  { id: "to2", title: "Selecting best practice template" },
  { id: "to3", title: "Collecting contract requirements" },
  { id: "to4", title: "Gathering use case details" },
  { id: "to5", title: "Capturing contract terms" },
  { id: "to6", title: "Generating filled template" },
  { id: "to7", title: "Creating contract summary PDF" },
];

export const RoleValidationAgentSteps = [
  { id: "jdc1", title: "Validating role" },
];

export const SowDocumentAnalyzerSteps = [
  { id: "sda0", title: "Analysing proposal document" },
  { id: "sda1", title: "Analysing SOW template sections" },
  { id: "sda2", title: "Generating required details" },
  { id: "sda3", title: "Checking for best practices" },
  { id: "sda4", title: "Drafting SOW document" },
];

// Individual RFQ workflow steps
export const RfqDocumentParserStep = {
  id: "rfq1",
  title: "Analyzing RFQ document",
};

export const RfqCriteriaParserStep = {
  id: "rfq2",
  title: "Parsing evaluation criteria",
};

export const RfqRequirementsExtractionStep = {
  id: "rfq3",
  title: "Extracting requirements and specifications",
};

export const RfqVendorEvaluationStep = {
  id: "rfq4",
  title: "Evaluating vendor proposals",
};

export const RfqResponseGenerationStep = {
  id: "rfq5",
  title: "Generating comprehensive response",
};

export const RfqQAStep = {
  id: "rfq6",
  title: "Analyzing RFQ document for Q&A",
};

export const RfqQAAgentStep = {
  id: "rfq7",
  title: "Answering your question",
};

// Full orchestrator flow (all steps in sequence)
export const RfqOrchestratorSteps = [
  RfqDocumentParserStep,
  RfqCriteriaParserStep,
  RfqRequirementsExtractionStep,
  RfqVendorEvaluationStep,
  RfqResponseGenerationStep,
];

export const WORKFLOW_SEQUENCE = [
  "default",
  "pr",
  "localizer",
  "mediaBrief",
  "socialMedia",
  "internalCom",
];

// Helper function to get appropriate social media steps based on image generation flag
export const getSocialMediaSteps = (isImageGeneration?: boolean): any[] => {
  return isImageGeneration
    ? socialMediaStepsWithImage
    : socialMediaStepsWithoutImage;
};

export const socialMediaSteps = socialMediaStepsWithImage; // Default to with image for backward compatibility

export const stepSequence = [
  prSteps,
  localizerSteps,
  mediaBriefSteps,
  socialMediaSteps,
  internalComSteps,
];

// Map agent IDs to their step groups
export const getStepsByAgentId = (
  agentId?: string,
  isImageGeneration?: boolean,
): any[] => {
  if (!agentId) return [];

  switch (agentId) {
    case "press_release_agent":
    case "PressReleaseAgent":
      return prSteps;
    case "LocalizerAgent":
    case "localizer_agent":
      return localizerSteps;
    case "media_brief_agent":
    case "MediaBriefAgent":
      return mediaBriefSteps;
    case "social_media_insights_agent":
    case "LinkedinAgent":
    case "SocialMediaInsightsAgent":
      return getSocialMediaSteps(isImageGeneration);
    case "internal_communication_agent":
    case "InternalCommunicationAgent":
      return internalComSteps;
    case "role_validation_failed":
      return RoleValidationAgentSteps;
    case "synthetic_resume_agent":
      return syntheticResumeSteps;
    case "resume_fetcher_agent":
      return resumeFetcherSteps;
    case "scoring_agent":
    case "scoring_agent_new":
      return scoringSteps;
    case "email_drafting":
      return emailDraftingSteps;
    case "modify_requirements":
      return modifyRequirementsSteps;
    case "translation_agent":
      return translationAgentSteps;
    case "contract_reviewer_qa_agent":
      return ContractReviewerQAAgentSteps;
    case "template_reviewer_checklist_agent":
      return checklistAgentSteps;
    case "template_reviewer_agent":
      return TemplateReviewerAgentSteps;
    case "template_guided_intake_agent":
      return TemplateGuidedIntakeAgentSteps;
    case "template_document_selector_agent":
      return TemplateDocumentSelectorSteps;
    case "template_drafter_agent":
      return TemplateDrafterSteps;
    case "template_reviewer_qa_agent":
      return TemplateReviewerQASteps;
    case "template_request_orchestrator":
      return []; // Don't show steps - sub-agents will show their own
    case "contract_reviewer_orchestrator":
      return ContractReviewerAgentSteps;
    case "legal_reviewer_orchestrator":
      return []; // Don't show steps - sub-agents will show their own
    case "consolidator_agent":
      return ConsolidatorAgentSteps;
    case "consolidator_agent_supplier_comm":
      return ConsolidatorAgentSupplierCommSteps;
    case "redrafting_agent":
      return RedraftingAgentSteps;
    case "sow_document_analyzer":
      return SowDocumentAnalyzerSteps;
    case "sow_pre_analyzer":
      return sowPreAnalyzerSteps;
    case "sow_data_modifier":
      return sowDataModifierSteps;
    case "sow_question_answerer":
      return sowQuestionAnswererSteps;
    case "sow_pii_detector":
      return sowPiiDetectorSteps;
    case "sow_auto_filler":
      return sowAutoFillerSteps;
    case "sow_final_generator":
      return sowFinalGeneratorSteps;
    case "jd_composer_agent":
    case "skill_inference_agent":
      return jdComposerSteps;
    case "rfq_orchestrator":
      return RfqOrchestratorSteps; // Full flow when agent is selected
    case "rfq_qa":
      return [RfqQAStep];
    case "rfq_qa_agent":
      return [RfqQAAgentStep];
    default:
      return [];
  }
};

export const getAgentStartedSteps = (
  agentId?: string,
  isImageGeneration?: boolean,
): any[] => {
  if (!agentId) return [];

  switch (agentId) {
    case "press_release_started":
      return prSteps;
    case "localizer_started":
      return localizerSteps;
    case "media_brief_started":
      return mediaBriefSteps;
    case "social_media_started":
      return getSocialMediaSteps(isImageGeneration);
    case "internal_communication_started":
      return internalComSteps;
    case "role_validation_failed":
      return RoleValidationAgentSteps;
    case "contract_reviewer_qa_agent":
      return ContractReviewerQAAgentSteps;
    case "synthetic_resume_agent":
      return syntheticResumeSteps; // Don't show steps during validation phase
    case "resume_fetcher_agent":
      return resumeFetcherSteps;
    case "scoring_agent":
    case "scoring_agent_new":
      return scoringSteps;
    case "email_drafting":
      return emailDraftingSteps;
    case "modify_requirements":
      return modifyRequirementsSteps;
    case "translation_started":
      return translationAgentSteps;
    case "template_reviewer_checklist_agent":
      return []; // Don't show steps for checklist agent - it's an internal step
    case "template_reviewer_started":
      return TemplateReviewerAgentSteps;
    case "template_reviewer_agent":
      return []; // Steps already shown by template_reviewer_started
    case "template_guided_intake_agent":
    case "template_guided_intake_started":
      return TemplateGuidedIntakeAgentSteps;
    case "template_document_selector_agent":
    case "template_document_selector_started":
      return TemplateDocumentSelectorSteps;
    case "template_drafter_agent":
    case "template_drafter_started":
      return TemplateDrafterSteps;
    case "template_reviewer_qa_agent":
    case "template_reviewer_qa_started":
      return TemplateReviewerQASteps;
    case "template_request_orchestrator":
    case "template_request_started":
      return []; // Don't show steps - sub-agents will show their own
    case "jd_composer_agent":
    case "skill_inference_agent":
      return jdComposerSteps;
    case "business_reviewer_started":
    case "BusinessReviewerAgent":
      return [
        ContractReviewerAgentSteps[0],
        ContractReviewerAgentSteps[4],
        ContractReviewerAgentSteps[5],
      ]; // Step 1: Business review, Step 5: Deviation analysis, Step 6: Negotiation points
    case "finance_reviewer_started":
      return [ContractReviewerAgentSteps[1]]; // Step 2: Finance review
    case "procurement_reviewer_started":
      return [ContractReviewerAgentSteps[2]]; // Step 3: Procurement review
    case "legal_reviewer_started":
      return [ContractReviewerAgentSteps[3]]; // Step 4: Legal review
    case "deviation_analysis_started":
    case "DeviationAnalysisAgent":
    case "DeviationAnalyzer":
    case "RFPDeviationAnalyzer":
    case "deviation_analyzer":
    case "rfp_deviation_analyzer":
    case "analyze_deviations":
      return [ContractReviewerAgentSteps[4]]; // Step 5: Deviation analysis
    case "negotiation_points_started":
    case "NegotiationPointsAgent":
    case "NegotiationPointsGenerator":
    case "negotiation_points_generator":
    case "generate_negotiation_points":
    case "legal_reviewer_orchestrator_started":
      return [];
    case "negotiation_generator":
      return [ContractReviewerAgentSteps[5]]; // Step 6: Negotiation points
    case "contract_reviewer_orchestrator_started":
      return []; // Don't show steps for orchestrator start
    case "consolidator_started":
      return ConsolidatorAgentSteps;
    case "consolidator_agent":
      return []; // Steps already shown by consolidator_started
    case "consolidator_agent_supplier_comm":
    case "consolidator_supplier_comm_started":
      return ConsolidatorAgentSupplierCommSteps;
    case "redrafting_agent":
    case "redrafting_started":
      return RedraftingAgentSteps;
    case "sow_document_analyzer":
      return SowDocumentAnalyzerSteps;
    case "sow_pre_analyzer":
      return sowPreAnalyzerSteps;
    case "sow_data_modifier":
      return sowDataModifierSteps;
    case "sow_question_answerer":
      return sowQuestionAnswererSteps;
    case "sow_pii_detector":
      return sowPiiDetectorSteps;
    case "sow_auto_filler_started":
      return sowAutoFillerSteps;
    case "sow_final_generator":
      return sowFinalGeneratorSteps;
    case "rfq_orchestrator":
    case "rfq_orchestrator_started":
      return []; // Orchestrator doesn't show steps - sub-agents handle it

    case "document_parser":
      return [RfqDocumentParserStep];

    case "criteria_parser":
    case "criteria_parser_agent":
      return [RfqCriteriaParserStep];

    case "rfq_qa":
      return [RfqQAStep];

    case "rfq_qa_agent":
      return [RfqQAAgentStep];

    default:
      return [];
  }
};

export const defaultMarketingPrompts: Prompt[] = [
  {
    title: "Product Launch & Update",
    prompt:
      "Create a multi-channel marketing campaign for product launch or update",
  },
  {
    title: "Partnership Announcement",
    prompt:
      "Create a multi-channel marketing campaign for strategic partnership announcement",
  },
  {
    title: "Leadership Announcement",
    prompt:
      "Create a multi-channel marketing campaign for leadership or executive appointment announcement",
  },
  {
    title: "Strategic Announcement",
    prompt:
      "Create a multi-channel marketing campaign for strategic vision announcement",
  },
];

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// Agent IDs that support file attachment functionality
export const AGENTS_WITH_FILE_ATTACHMENT = [11, 7, 20, 18, 21, 26, 46, 44, 47, 35, 37, 38, 40, 41, 57, 60];

// Agent IDs that should keep HR filters visible even after response
export const AGENTS_WITH_PERSISTENT_HR_FILTERS = [
  "no_candidates_found",
  "validation_check",
];

// Filter options for HR Agent
export const EXPERIENCE_OPTIONS = [
  { label: "0-3 years", value: "0-3" },
  { label: "3-10 years", value: "3-10" },
  { label: "10+ years", value: "10+" },
];

export const LOCATION_OPTIONS = [
  { label: "UAE", value: "uae" },
  { label: "Australia", value: "australia" },
  { label: "Bangladesh", value: "bangladesh" },
  { label: "Brazil", value: "brazil" },
  { label: "Canada", value: "canada" },
  { label: "Egypt", value: "egypt" },
  { label: "France", value: "france" },
  { label: "Germany", value: "germany" },
  { label: "India", value: "india" },
  { label: "Italy", value: "italy" },
  { label: "Jordan", value: "jordan" },
  { label: "Lebanon", value: "lebanon" },
  { label: "Malaysia", value: "malaysia" },
  { label: "Nigeria", value: "nigeria" },
  { label: "Philippines", value: "philippines" },
  { label: "Pakistan", value: "pakistan" },
  { label: "South Africa", value: "southafrica" },
  { label: "Sri Lanka", value: "srilanka" },
  { label: "Turkey", value: "turkey" },
  { label: "United Kingdom", value: "unitedkingdom" },
  { label: "United States", value: "unitedstates" },
  { label: "Others", value: "others" },
];

export const DEGREE_OPTIONS = [
  {
    label: "Bachelor’s in Computer Science / Data Science",
    value: "bachelorsincomputersciencedatascience",
  },
  {
    label: "Bachelor’s in Business Administration",
    value: "bachelorsinbusinessadministration",
  },
  {
    label: "Bachelor’s in Engineering",
    value: "bachelorsinengineering",
  },
  {
    label: "Bachelor’s in Finance / Economics",
    value: "bachelorsinfinanceeconomics",
  },
  {
    label: "Bachelor’s in Marketing",
    value: "bachelorsinmarketing",
  },
  {
    label: "Bachelor’s in Healthcare-related fields",
    value: "bachelorsinhealthcarerelatedfields",
  },
  {
    label: "Bachelor’s in Hospitality",
    value: "bachelorsinhospitality",
  },
  {
    label: "Bachelor’s in Law",
    value: "bachelorsinlaw",
  },
  {
    label: "Master’s in Computer Science / Data Science",
    value: "mastersincomputersciencedatascience",
  },
  {
    label: "Master’s in Business Administration (MBA)",
    value: "mastersinbusinessadministrationmba",
  },
  { label: "Master’s Degree", value: "mastersdegree" },
  { label: "PhD", value: "phd" },
  { label: "Others", value: "others" },
];

export const PREVIOUS_COMPANY_OPTIONS = [
  { label: "G42 Companies", value: "g42companies" },
  { label: "Abu Dhabi Companies", value: "abudhabicompanies" },
  { label: "Accenture", value: "accenture" },
  { label: "Bain & Company", value: "baincompany" },
  {
    label: "Boston Consulting Group (BCG)",
    value: "bostonconsultinggroupbcg",
  },
  { label: "Cisco", value: "cisco" },
  { label: "Citibank", value: "citibank" },
  { label: "Deloitte", value: "deloitte" },
  { label: "EY (Ernst & Young)", value: "eyernstyoung)" },
  { label: "Google", value: "google" },
  { label: "HSBC", value: "hsbc" },
  { label: "IBM", value: "ibm" },
  { label: "J.P. Morgan", value: "jpmorgan" },
  { label: "KPMG", value: "kpmg" },
  { label: "McKinsey & Company", value: "mckinseycompany" },
  { label: "Microsoft", value: "microsoft" },
  { label: "Oracle", value: "oracle" },
  { label: "Procter & Gamble (P&G)", value: "proctergamblepg" },
  { label: "PwC", value: "pwc" },
  { label: "Schlumberger", value: "schlumberger" },
  { label: "Siemens", value: "siemens" },
  { label: "Standard Chartered", value: "standardchartered" },
  { label: "Others", value: "others" },
];

export const PROCUREMENT_TYPE_OPTIONS = [
  {
    label: "Contract Request (SOW Creator) Agent",
    value: "sow_filler",
    placeholder:
      "What do you want the contract template to be for? (e.g., a cloud-based CRM for Core42)",
  },
  {
    label: "Contract Proposal Review Agent",
    value: "reviewer",
    placeholder:
      "I will perform a business, procurement, finance, and legal review of your contract proposal. Please upload the document.",
  },
  {
    label: "Final Contract Redraft Agent",
    value: "redraft",
    placeholder: "Upload negotiation list and contract proposal",
  },
];

export const CONTRACT_TYPE_OPTIONS = [
  { label: "Employment Contracts", value: "employment" },
  { label: "Purchase Agreements", value: "purchase" },
  { label: "Lease Agreements", value: "lease" },
  { label: "Service Agreements", value: "service" },
];

export const GROUP_COMPANY_OPTIONS = [
  { label: "Group 42 Holding Ltd", value: "group42holding" },
  { label: "Core42", value: "core42" },
  { label: "Inception", value: "inception" },
  { label: "Injazat", value: "injazat" },
  { label: "Khazna Data Centers", value: "khazna" },
  { label: "M42", value: "m42" },
  { label: "Presight AI Holding PLC", value: "presight" },
  { label: "CPX", value: "cpx" },
  // { label: "Space42", value: "space42" },
  { label: "AIQ", value: "aiq" },
];

// Agent-specific text configuration
export interface AgentTextConfig {
  subtitle: string;
  promptTitle: string;
  inputPlaceholder: string;
}

export const AGENT_TEXT_CONFIG: Record<string, AgentTextConfig> = {
  "11": {
    subtitle: "Your intelligent marketing assistant that produces:",
    promptTitle: "What type of announcement would you like to make today?",
    inputPlaceholder:
      "Start with your prompt— the agent will gather any details needed for quality output, as needed ...",
  },
  "18": {
    subtitle:
      "Your intelligent recruiter agent that helps you efficiently reconnect with qualified previous candidates for new roles.",
    promptTitle: "What role are you trying to fill today?",
    inputPlaceholder: `Enter the job title and select the minimum qualification from the filters. You can upload new resumes or search Accellor resume database.`,
  },
  "7": {
    subtitle:
      "Your intelligent agent for translating general and legal documents.",
    promptTitle: "What type of translation you would like to do today?",
    inputPlaceholder:
      "Upload your document or enter your text here for translation",
  },
  "106": {
    subtitle:
      "Your intelligent agent for generating social media posts for X, Instagram and LinkedIn.",
    promptTitle: "What would you like to share on social media today?",
    inputPlaceholder: "Type your message here...",
  },
  "20": {
    subtitle:
      "Your intelligent agent for reviewing contract proposals and redrafting",
    promptTitle: "",
    inputPlaceholder:
      "I will perform a business, procurement, finance, and legal review of your contract proposal. Please upload the document.",
  },
  "21": {
    subtitle: "Your intelligent agent for generating SOW documents",
    promptTitle: "",
    inputPlaceholder: "Please upload the proposal document",
  },
  "46": {
    subtitle: "Your intelligent agent for reviewing contract templates",
    promptTitle: "",
    inputPlaceholder: "Please upload the contract template to start reviewing",
  },
  "23": {
    subtitle:
      "Your intelligent agent for composing comprehensive job descriptions with required skills and qualifications.",
    promptTitle: "Which job description you want to generate?",
    inputPlaceholder:
      "Enter the job title or role description to compose a complete job description...",
  },
  "24": {
    subtitle:
      "Your intelligent agent for inferring required skills and qualifications from job roles.",
    promptTitle: "Which job role would you like to analyze?",
    inputPlaceholder:
      "Enter the job title or role to infer required skills and qualifications...",
  },
  "25": {
    subtitle:
      "Your intelligent executive assistant for CEO's office operations.",
    promptTitle: "How can I assist you today?",
    inputPlaceholder: "Type your message here...",
  },
  "31": {
    subtitle: "Your intelligent assistant for smart meeting briefs.",
    promptTitle: "How can I assist you today?",
    inputPlaceholder: "Type your message here...",
  },
  "32": {
    subtitle: "Your intelligent assistant for news and tech trend watch.",
    promptTitle: "How can I assist you today?",
    inputPlaceholder: "Type your message here...",
  },
  "33": {
    subtitle: "Your intelligent assistant for workflow operation.",
    promptTitle: "How can I assist you today?",
    inputPlaceholder: "Type your message here...",
  },
  "1": {
    subtitle:
      "Your intelligent press release and marketing assistant that produces:",
    promptTitle: "What type of announcement would you like to make today?",
    inputPlaceholder:
      "Start with your prompt— the agent will gather any details needed for quality output, as needed ...",
  },
  "44": {
    subtitle:
      "Your intelligent agent for analyzing RFQs and generating responses",
    promptTitle: "",
    inputPlaceholder:
      "Please upload the RFQ document for evaluation and response generation",
  },
  "47": {
    subtitle:
      "Your intelligent agent for verifying contracts from a legal point of view",
    promptTitle: "",
    inputPlaceholder:
      "Upload your document for legal review and compliance check",
  },
  "26": {
    subtitle: "Your intelligent procurement assistant that produces:",
    promptTitle: "What would you like me to help you with today?",
    inputPlaceholder: "Type your message here...",
  },
  "35": {
    subtitle:
      "Your intelligent AI agent screener that evaluates AI agent proposals against job requirements.",
    promptTitle: "What AI agent proposal would you like me to screen?",
    inputPlaceholder:
      "Please enter the job title and upload agent profile to start",
  },
  "37": {
    subtitle:
      "Your intelligent job intake agent that captures and structures job requirements.",
    promptTitle: "What role are you looking to fill?",
    inputPlaceholder:
      "Describe the job role, requirements, and key responsibilities you need help structuring...",
  },
  "38": {
    subtitle:
      "Your intelligent skill gap assessment agent that identifies skill gaps in your workforce.",
    promptTitle: "Which employee or team would you like to assess?",
    inputPlaceholder:
      "Upload the employee profile/resume or enter the employee name, current role, and target role to identify skill gaps and development needs...",
  },
  "39": {
    subtitle:
      "Your intelligent talent scouting agent that helps find and identify top candidates.",
    promptTitle: "What type of talent are you looking to scout?",
    inputPlaceholder:
      "Describe the role, skills, and experience you're looking for in ideal candidates...",
  },
  "40": {
    subtitle:
      "Your intelligent human capital skill intelligence agent that maps skills to roles and proficiency levels.",
    promptTitle: "Which role would you like to benchmark?",
    inputPlaceholder:
      "Enter the job description or paste the JD to generate a comprehensive skills and proficiency benchmark...",
  },
  "41": {
    subtitle:
      "Your intelligent interview question design agent that creates tailored interview questions.",
    promptTitle: "What role are you interviewing for?",
    inputPlaceholder:
      "Upload Job Description, Candidate Resume, and Pre-Screening Questions (optional) to generate a customized interview pack...",
  },
  "60": {
    subtitle:
      "Your intelligent interview question design agent that creates tailored interview questions.",
    promptTitle: "What role are you interviewing for?",
    inputPlaceholder:
      "Upload Job Description, Candidate Resume, and Pre-Screening Questions (optional) to generate a customized interview pack...",
  },
  "42": {
    subtitle:
      "Your intelligent training search agent that finds relevant training resources for skill development.",
    promptTitle: "What skills do you need training for?",
    inputPlaceholder:
      "Enter the skill areas, proficiency level, and learning objectives to find suitable training programs...",
  },
  "57": {
    subtitle:
      "Your intelligent learning path agent that generates personalized learning paths based on skill gap assessments.",
    promptTitle: "What learning path would you like to create?",
    inputPlaceholder:
      "Enter the employee name, current role, target role, or upload a skill gap assessment to generate a personalized learning path...",
  },
  "45": {
    subtitle:
      "Your intelligent agent that captures sourcing details, generates an SOW, and creates an evaluation package",
    promptTitle: "",
    inputPlaceholder:
      "Describe your RFQ requirements to build the SOW and evaluation package (e.g., we need to procure a cloud-based CRM for Accellor)",
  },
};

// Default values
export const DEFAULT_AGENT_TEXT: AgentTextConfig = {
  subtitle: "",
  promptTitle: "",
  inputPlaceholder: "Type your message here...",
};

// Flow steps configuration for agent cards
export const AGENT_FLOW_STEPS: Record<
  string,
  Array<{ id: number; text: string }>
> = {
  "18": [
    { id: 1, text: "Maps role to core & future-ready skills " },
    { id: 2, text: "Screens resumes from uploads and internal Accellor database" },
    { id: 3, text: "Filters for past interview feedback" },
    // { id: 4, text: "Overlays current role, title, location " },
    { id: 4, text: "Evaluates fit for Round 1 interview" },
    { id: 5, text: "Shortlists candidates, initiates emails" },
  ],
  "7": [
    { id: 1, text: "Takes the input document or content" },
    { id: 2, text: "Understands the source and target language" },
    { id: 3, text: "Understands the translation context" },
    { id: 4, text: "Highlights areas for user review" },
    { id: 5, text: "Generates the translated output" },
  ],
  "106": [
    { id: 1, text: "Receives and validates content" },
    { id: 2, text: "Understands intent and context" },
    { id: 3, text: "Extracts key messages and themes" },
    { id: 4, text: "Adapts content per platform " },
    { id: 5, text: "Generates platform-ready social posts" },
  ],
  "20": [
    {
      id: 1,
      text: "Provides Business, Finance, Procurement, Legal review of contract proposal",
    },
    {
      id: 2,
      text: "Identifies deviations, key gaps and risks",
    },
    {
      id: 3,
      text: "Consolidates functional review inputs to prepare negotiation list",
    },
    { id: 4, text: "Redrafts clean contract copy post negotiation inputs" },
  ],
  "21": [
    { id: 1, text: "Intakes sourcing details from user" },
    {
      id: 2,
      text: "Selects business unit & category specific best practices template",
    },
    { id: 3, text: "Drafts contract templates from user input and proposals" },
    {
      id: 4,
      text: "Answers user questions related to SOW template",
    },
    {
      id: 5,
      text: "Drafts SOW template based on input proposal and selected template",
    },
  ],
  "46": [
    { id: 1, text: "Intakes contract file from user" },
    { id: 2, text: "Runs compliance scan against checklist" },
    { id: 3, text: "Highlights risks and deviations" },
    { id: 4, text: "Generates actionable smart recommendations" },
    { id: 5, text: "Produces final reviewed output" },
  ],
  "23": [
    { id: 1, text: "Receives job role as input" },
    { id: 2, text: "Analyzes the role requirements" },
    { id: 3, text: "Identifies required skills and qualifications" },
    { id: 4, text: "Creates comprehensive job description" },
  ],
  "24": [
    { id: 1, text: "Analyzes job role and requirements" },
    { id: 2, text: "Identifies core functional and technical skills" },
    { id: 3, text: "Maps future-ready skills" },
    { id: 4, text: "Establishes required qualifications" },
    { id: 5, text: "Generates preferred qualifications" },
  ],
  "25": [
    { id: 1, text: "Processes executive requests" },
    { id: 2, text: "Analyzes business intelligence" },
    { id: 3, text: "Prepares strategic insights" },
    { id: 4, text: "Coordinates office operations" },
    { id: 5, text: "Delivers executive summaries" },
  ],
  "31": [
    { id: 1, text: "Processes executive requests" },
    { id: 2, text: "Analyzes business intelligence" },
    { id: 3, text: "Prepares strategic insights" },
    { id: 4, text: "Coordinates office operations" },
    { id: 5, text: "Delivers executive summaries" },
  ],
  "32": [
    { id: 1, text: "Processes executive requests" },
    { id: 2, text: "Analyzes business intelligence" },
    { id: 3, text: "Prepares strategic insights" },
    { id: 4, text: "Coordinates office operations" },
    { id: 5, text: "Delivers executive summaries" },
  ],
  "33": [
    { id: 1, text: "Processes executive requests" },
    { id: 2, text: "Analyzes business intelligence" },
    { id: 3, text: "Prepares strategic insights" },
    { id: 4, text: "Coordinates office operations" },
    { id: 5, text: "Delivers executive summaries" },
  ],
  "1": [
    { id: 1, text: "Create press release" },
    { id: 2, text: "Translates to arabic" },
    { id: 3, text: "Creates media Q&A brief" },
  ],
  "44": [
    { id: 1, text: "Receives and analyzes RFQ document" },
    { id: 2, text: "Evaluates requirements and specifications" },
    { id: 3, text: "Assesses compliance and feasibility" },
    { id: 4, text: "Generates comprehensive response" },
    { id: 5, text: "Provides recommendations and next steps" },
  ],
  "47": [
    { id: 1, text: "Receives and analyzes contract document" },
    { id: 2, text: "Reviews legal clauses and terms" },
    { id: 3, text: "Checks regulatory compliance" },
    { id: 4, text: "Identifies legal risks and gaps" },
    { id: 5, text: "Provides legal recommendations" },
  ],
  "26": [
    { id: 1, text: "Intent classification" },
    { id: 2, text: "Query planner" },
    { id: 3, text: "SQL generator" },
    { id: 4, text: "Answering agent" },
  ],
  "45": [
    { id: 1, text: "Captures sourcing details from user" },
    { id: 2, text: "Collects project & goods/services requirements" },
    { id: 3, text: "Generates SOW draft using best-practice template" },
    { id: 4, text: "Generates and downloads final SOW PDF" },
    { id: 5, text: "Generates evaluation package for vendor assessment" },
  ],
};

// Procurement Dashboard Constants
export const g42_companies = {
  analog_studios_fz_llc: "Analog Studios FZ LLC",
  core42_holdings_us_llc: "Core42 Holdings US LLC",
  core42_technology_projects_llc: "Core42 Technology Projects LLC",
  ct_corporate_services_llc: "CT Corporate Services LLC",
  daypop_technology_projects_llc: "DayPop Technology Projects LLC",
  g_forty_two_general_trading_llc: "G Forty Two General Trading LLC",
  g42_cloud_technology_llc: "G42 Cloud Technology LLC",
  g42_india_technology_private_limited: "G42 India Technology Private Limited",
  g42_international_holding_rsc_ltd: "G42 International Holding RSC Ltd",
  g42_sky20_technological_projects_llc: "G42 Sky20 Technological Projects L.L.C.",
  g42_sky21_technological_projects_llc: "G42 Sky21 Technological Projects LLC",
  g42_sky7_technological_projects_llc: "G42 SKY7 TECHNOLOGICAL PROJECTS L.L.C.",
  group_42_uk_limited: "Group 42 UK Limited",
  i42_us_dc_holding_llc: "I42 US DC holding LLC",
  inception_ai_investments_ltd: "Inception AI Investments Ltd",
  inception_ai_ip_ltd: "Inception AI IP Ltd",
  inception_ai_r_and_d_fz_llc: "INCEPTION AI R AND D FZ LLC",
  inception_artificial_intelligence_llc_opc: "Inception Artificial Intelligence LLC OPC",
  inception_holding_rsc_ltd: "INCEPTION HOLDING RSC LTD",
  inception_institute_of_artificial_intelligence_ltd: "INCEPTION INSTITUTE OF ARTIFICIAL INTELLIGENCE LTD",
  injazat_data_systems_llc: "Injazat Data Systems LLC",
};

export const g42_entities = {
  "1070_inception_ai_investments_ltd": "1070-Inception AI Investments Ltd",
  "42a_us_holding_llc": "42A US Holding LLC",
  analog_artificial_intelligence_llc_spc: "Analog Artificial Intelligence-L.L.C-S.P.C",
  analog_studios_fz_llc: "Analog Studios FZ LLC",
  core42_fz_llc: "Core42 FZ LLC",
  core42_holding_rsc_ltd: "Core42 Holding RSC Ltd",
  core42_holding_us_llc: "Core42 Holding US LLC",
  core42_technology_projects_llc: "Core42 Technology Projects LLC",
  cpx_holding_llc_opc: "CPX Holding – L.L.C. – O.P.C",
  ct_corporate_services_llc: "CT Corporate Services LLC",
  daypop_technology_projects_llc: "DayPop Technology Projects LLC",
  expansion_project_technologies_holding_11_spv_rsc_ltd: "Expansion Project Technologies Holding 11 SPV RSC Ltd",
  g_forty_two_general_trading_llc: "G Forty Two General Trading - LLC",
  g42_capital_limited: "G42 CAPITAL LIMITED",
  g42_cloud_technology_llc: "G42 Cloud Technology LLC",
  g42_communications_llc: "G42 Communications LLC",
  g42_companies_management_limited: "G42 Companies Management Limited",
  g42_infrastructure_ai_holding_rsc_ltd: "G42 Infrastructure AI Holding RSC Ltd",
  g42_pharmaceutical_manufacturing_llc: "G42 Pharmaceutical Manufacturing LLC",
  g42_sky21_technological_projects_llc: "G42 Sky21 Technological Projects LLC",
  g42_sky7_technological_projects_llc: "G42 Sky7 Technological Projects LLC",
  global_valley_technological_projects_sole_proprietorship_llc: "Global Valley Technological Projects - Sole Proprietorship LLC",
  group_42_holding_ltd: "Group 42 Holding Ltd",
  gx_investments_us_llc: "GX Investments US LLC",
  hassantuk: "Hassantuk",
  i42_us_dc_holding_llc: "I42 US DC Holding LLC",
  inception_ai_investments_ltd: "Inception AI Investments Ltd",
  inception_ai_r_and_d_fz_llc: "Inception AI R and D FZ LLC",
  inception_artificial_intelligence_llc_opc: "Inception Artificial Intelligence LLC OPC",
  inception_holding_rsc_ltd: "Inception Holding RSC Ltd",
  inception_institute_of_artificial_intelligence_ltd: "Inception Institute of Artificial Intelligence Ltd",
  injazat_data_systems_llc: "Injazat Data Systems LLC",
  makhzan1_technologies_llc_spc: "Makhzan1 Technologies LLC SPC",
  mozn_holding_limited: "MOZN Holding Limited",
  pivot_asset_holding_rsc_ltd: "Pivot Asset Holding RSC Ltd",
  tsi_middle_east_information_technology_solutions_llc: "TSI Middle East Information Technology Solutions LLC",
};

export const g42_contract_owner = {
  adele_herlihy: "Adele Herlihy",
  adrian_hobbs: "Adrian Hobbs",
  aislinn_barr: "Aislinn Barr",
  alfred_alava: "Alfred Alava",
  ali_jibran: "Ali Jibran",
  amy_lehoczky: "Amy Lehoczky",
  andrew_chacko: "Andrew Chacko",
  anni_gupta: "Anni Gupta",
  ashish_ramsurrun: "Ashish Ramsurrun",
  bhavuk_arora: "Bhavuk Arora",
  bonnie_wortha: "Bonnie Wortha",
  bradley_worrall: "Bradley Worrall",
  chandni_javeri: "Chandni Javeri",
  coco_jin: "Coco Jin",
  danish_kadiri: "Danish Kadiri",
  david_power: "David Power",
  dilip_vyas: "Dilip Vyas",
  divyesh_mahajan: "Divyesh Mahajan",
  dr_mike_roshchin: "Dr. Mike Roshchin",
  elmer_barboza: "Elmer Barboza",
  farrukh_zaheer: "Farrukh Zaheer",
  firas_khair: "Firas Khair",
  grzegorz_kosior: "Grzegorz Kosior",
  hatem_saber: "Hatem Saber",
  ignasi_ferrer_gomez: "Ignasi Ferrer Gomez",
  jafeen_jamaludeen: "Jafeen Jamaludeen",
  jaydeep_pandat: "Jaydeep Pandat",
  joanne_norman: "Joanne Norman",
  john_franklin: "John Franklin",
  kathleen_meneses: "Kathleen Meneses",
  lauren_phillips: "Lauren Phillips",
  louella_thomas: "Louella Thomas",
  lubna_zegabrah: "lubna Zegabrah",
  lucy_browne: "Lucy Browne",
  maria_rogacion: "Maria Rogacion",
  maxim_zhurbin: "Maxim Zhurbin",
  mothasim_kozhipurath: "Mothasim Kozhipurath",
  needhish_kuruvilla: "Needhish Kuruvilla",
  neha_verma: "Neha Verma",
  niall_o_broin: "Niall Ó Broin",
  noaf_moussa: "Noaf Moussa",
  paulo_mata: "Paulo Mata",
  petro_elmelegey: "Petro Elmelegey",
  poojitha_lokanath: "Poojitha Lokanath",
  rajinder_kanda: "Rajinder Kanda",
  rasha_guirguis: "Rasha Guirguis",
  rohit_manucha: "Rohit Manucha",
  sedef_demircan: "Sedef Demircan",
  shabeerali_kunhiriam: "Shabeerali Kunhiriam",
  sheba_abraham: "Sheba Abraham",
  shiju_badarudeen: "Shiju Badarudeen",
  shubhanshi_gupta: "Shubhanshi Gupta",
  sian_warren: "Sian Warren",
  tarun_gupta: "Tarun Gupta",
  taybah_alameri: "Taybah Alameri",
  tina_mohandas: "Tina Mohandas",
};
